/**
 * Conversion analytics for documentiq.algoscale.com.
 *
 * GA4 is wired in index.html (G-QMCR08LF2G). This file is the single
 * place we send custom events. Two reasons it exists:
 *
 *   1. Avoid scattering window.gtag(...) calls across components — they
 *      drift, miss the right param shape, and silently no-op in tests.
 *   2. Centralise the funnel definition so when marketing asks "what
 *      counts as a conversion?" we point at one file.
 *
 * Events we track (mark as conversions in GA4 → Admin → Events):
 *   - page_view            : every SPA navigation (router subscribe) — GA's
 *                            auto page_view only fires on hard reloads,
 *                            which silently drops 90%+ of marketing traffic
 *   - signup_cta_click     : user clicked any "Start for Free" / register link
 *   - signup_completed     : POST /auth/register returned 200 (fired from useAuth)
 *   - signup_failed        : POST /auth/register errored (funnel diagnostics)
 *   - contact_sales_click  : user clicked our contact-sales mailto
 *   - login_completed      : returning-user login succeeded (for context)
 *   - login_failed         : login error (funnel diagnostics)
 *   - enterprise_inquiry_click : fake-door validation for unbuilt
 *                            features. Params: `capability` (one of
 *                            'continuous_fine_tuning' | 'email_intake' |
 *                            'both') and `source` (hero, enterprise_section,
 *                            enterprise_section_footer). Watch for 4–6
 *                            weeks before deciding to build either.
 *
 * The CTA-click events come from a single delegated listener installed
 * by `initAnalytics()` so we don't have to wire onClick on every Link.
 */

// Recipient for "contact sales" mailto links. Exported so React
// components render the same address the tracker matches on — if you
// ever change this, the tracker doesn't silently stop counting clicks.
export const CONTACT_SALES_EMAIL = 'askus@algoscale.com'

type EventParams = Record<string, string | number | boolean | undefined>

type Gtag = (
  command: 'event' | 'config' | 'js',
  action: string,
  params?: EventParams,
) => void

// Minimal shape of the router object we need. Matches what
// createBrowserRouter returns — we only call subscribe + read state.
type RouterLike = {
  subscribe: (fn: (state: { location: { pathname: string; search: string } }) => void) => () => void
  state: { location: { pathname: string; search: string } }
}

declare global {
  interface Window {
    gtag?: Gtag
    // Stamped on first init so HMR / double-mounts don't double-bind.
    __dqAnalyticsBound?: boolean
    __dqPageViewsBound?: boolean
  }
}

export function track(event: string, params: EventParams = {}): void {
  if (typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return
  window.gtag('event', event, params)
}

/**
 * Install the one-shot delegated click listener.
 *
 * Walks up from the click target looking for the nearest <a>, then
 * inspects href/getAttribute('href') to decide whether the click is
 * a tracked CTA. Using event delegation (rather than onClick on each
 * Link) keeps Landing.tsx — which has 9+ CTAs — clean.
 *
 * Called once from main.tsx.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return
  if (window.__dqAnalyticsBound) return
  window.__dqAnalyticsBound = true

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      const anchor = target.closest('a') as HTMLAnchorElement | null
      if (!anchor) return

      // React Router <Link> renders an <a>. We read getAttribute('href')
      // rather than anchor.href because the latter resolves to the
      // absolute URL, defeating string-prefix comparisons.
      const href = anchor.getAttribute('href') || ''

      if (href === '/register' || href.startsWith('/register?')) {
        track('signup_cta_click', {
          cta_text: (anchor.textContent || '').trim().slice(0, 60),
          cta_location: window.location.pathname,
        })
        return
      }

      if (href.startsWith(`mailto:${CONTACT_SALES_EMAIL}`)) {
        track('contact_sales_click', {
          cta_text: (anchor.textContent || '').trim().slice(0, 100),
          cta_location: window.location.pathname,
        })
        return
      }
    },
    // Capture phase so React's synthetic-event system can't preventDefault
    // before we record the click. We never preventDefault ourselves.
    true,
  )
}

/**
 * Fire GA4 page_view on every React Router navigation.
 *
 * Why this exists: GA4's auto page_view fires once per hard reload. In
 * an SPA, the user can read five blog posts without GA seeing a single
 * page_view past the first. That makes every per-page metric (engaged
 * sessions, pages-per-session, top content) wildly wrong. Subscribing
 * to the router and firing a manual page_view event closes the gap.
 *
 * Called once from main.tsx with the imported router instance.
 */
export function trackPageViews(router: RouterLike): void {
  if (typeof window === 'undefined') return
  if (window.__dqPageViewsBound) return
  window.__dqPageViewsBound = true

  // The initial pageload is already counted by GA's auto page_view from
  // the gtag('config') call in index.html, so we only fire on subsequent
  // navigations. Remember the path we started on and skip the first
  // subscribe callback that matches it.
  let lastPath = router.state.location.pathname + router.state.location.search

  router.subscribe((state) => {
    const path = state.location.pathname + state.location.search
    if (path === lastPath) return
    lastPath = path
    // The router subscribe callback fires synchronously during the
    // navigation commit, before React renders the new route and its
    // useSEO effect updates document.title. Deferring to the next
    // macrotask lets useSEO run first so we record the new page's
    // title, not the previous one.
    setTimeout(() => {
      track('page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      })
    }, 0)
  })
}
